import React, { useState } from 'react';

export default function Skills() {
  const [activeCategory, setActiveCategory] = useState('backend');

  const skillCategories = {
    backend: {
      title: "Backend Development",
      icon: "⚙️",
      skills: [
        { name: "Java", level: 95, years: "8+ years" },
        { name: "Spring Boot", level: 90, years: "6+ years" },
        { name: "Microservices", level: 85, years: "4+ years" },
        { name: "REST APIs", level: 90, years: "6+ years" },
        { name: "Hibernate/JPA", level: 85, years: "5+ years" },
        { name: "Kafka", level: 80, years: "3+ years" }
      ]
    },
    frontend: {
      title: "Frontend Development",
      icon: "🎨",
      skills: [
        { name: "React", level: 80, years: "3+ years" },
        { name: "JavaScript", level: 85, years: "5+ years" },
        { name: "HTML/CSS", level: 90, years: "8+ years" },
        { name: "Tailwind CSS", level: 85, years: "2+ years" },
        { name: "Next.js", level: 70, years: "2+ years" },
        { name: "jQuery", level: 80, years: "4+ years" }
      ]
    },
    cloud: {
      title: "Cloud & DevOps",
      icon: "☁️",
      skills: [
        { name: "AWS", level: 80, years: "3+ years" },
        { name: "Docker", level: 75, years: "3+ years" },
        { name: "Jenkins", level: 70, years: "2+ years" },
        { name: "CI/CD", level: 75, years: "3+ years" },
        { name: "ECS/Fargate", level: 80, years: "2+ years" },
        { name: "SQS/SNS", level: 75, years: "2+ years" }
      ]
    },
    database: {
      title: "Databases",
      icon: "💾",
      skills: [
        { name: "MySQL", level: 85, years: "6+ years" },
        { name: "PostgreSQL", level: 80, years: "4+ years" },
        { name: "MongoDB", level: 75, years: "3+ years" },
        { name: "Redis", level: 70, years: "2+ years" },
        { name: "H2 Database", level: 85, years: "4+ years" }
      ]
    }
  };

  return (
    <section className="min-h-screen bg-gradient-to-br from-slate-50 to-gray-100 py-20 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            Technical Skills
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Mastering the full stack with expertise in backend development, cloud technologies, and modern frameworks
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {Object.entries(skillCategories).map(([key, category]) => (
            <button
              key={key}
              onClick={() => setActiveCategory(key)}
              className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 flex items-center space-x-2 ${
                activeCategory === key
                  ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white shadow-lg scale-105'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <span>{category.icon}</span>
              <span>{category.title}</span>
            </button>
          ))}
        </div>

        {/* Skills Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {skillCategories[activeCategory].skills.map((skill, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-semibold text-gray-900">{skill.name}</h3>
                <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                  {skill.years}
                </span>
              </div>
              
              {/* Progress Bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className="bg-gradient-to-r from-blue-500 to-purple-600 h-3 rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${skill.level}%` }}
                ></div>
              </div>
              
              <div className="flex justify-between text-sm text-gray-600">
                <span>Proficiency</span>
                <span className="font-semibold">{skill.level}%</span>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Skills */}
        <div className="mt-16 bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">Additional Expertise</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="p-4">
              <div className="text-3xl mb-2">📡</div>
              <h4 className="font-semibold text-gray-900">Protocols</h4>
              <p className="text-sm text-gray-600">SIGTRAN, SCTP, HTTP, WebSocket</p>
            </div>
            <div className="p-4">
              <div className="text-3xl mb-2">🏗️</div>
              <h4 className="font-semibold text-gray-900">Architecture</h4>
              <p className="text-sm text-gray-600">System Design, HLD, LLD</p>
            </div>
            <div className="p-4">
              <div className="text-3xl mb-2">🔧</div>
              <h4 className="font-semibold text-gray-900">Tools</h4>
              <p className="text-sm text-gray-600">Git, Maven, Postman, JConsole</p>
            </div>
            <div className="p-4">
              <div className="text-3xl mb-2">📊</div>
              <h4 className="font-semibold text-gray-900">Concepts</h4>
              <p className="text-sm text-gray-600">DSA, OOP, Multithreading</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}